import React, { useState, useEffect, useRef, useCallback } from 'react';
import './LoginHeroDemo.css';

/**
 * LoginHeroDemo — Claude-Code-style hero terminal (matches the promo-ad
 * TerminalWindow layout). A MIX of two entry kinds rotates through the input:
 *
 *   - 'cmd'  — a real `!` shell command you run in Claude Code (setup: init,
 *              mcp install). The conversation area shows the command's output.
 *   - 'chat' — a plain-English request; Claude replies like a real LLM turn
 *              (prose + a Zibby MCP tool call + result). This is the agent-native
 *              path that replaced the raw `zibby agent new/deploy/trigger` CLI.
 *
 * The conversation area scrolls if a turn is long. AUTO advance types the input
 * + streams the response; MANUAL (‹ ›) fades the finished turn in. Hover pauses.
 * Copy (on `cmd` only) strips the leading `!`. Prev version: *.bash.legacy.js.bak.
 */
const DEMOS = [
  {
    type: 'cmd',
    input: 'npx @zibby/cli init',
    lines: [
      { kind: 'out', text: 'Scaffolded .claude/ · 29 commands · 2 subagents' },
      { kind: 'out', text: 'Wrote AGENTS.md + .cursor/rules' },
      { kind: 'ok', text: 'Zibby ready in this project' },
    ],
  },
  {
    type: 'cmd',
    input: 'zibby mcp install --agent claude-code',
    lines: [
      { kind: 'ok', text: 'Zibby MCP installed · 12 tools live' },
      { kind: 'dim', text: 'Reopen Claude Code to pick them up' },
    ],
  },
  {
    type: 'chat',
    input: 'Build an agent that reviews every PR',
    lines: [
      { kind: 'say', text: "Sure — I'll create a code-review agent and wire it to your pull requests." },
      { kind: 'tool', tool: 'create_agent', result: 'Created · code-review' },
      { kind: 'say', text: 'Done. It reviews every new PR. Want me to deploy it?' },
    ],
  },
  {
    type: 'chat',
    input: 'Yes, deploy it to the cloud',
    lines: [
      { kind: 'say', text: 'Deploying code-review to the Zibby cloud…' },
      { kind: 'tool', tool: 'deploy_agent', result: 'Live · code-review.zibby.run' },
    ],
  },
  {
    type: 'chat',
    input: 'Run it on acme/api PR #317',
    lines: [
      { kind: 'tool', tool: 'trigger_agent', result: 'Review posted · 3 comments' },
      { kind: 'say', text: 'Reviewed #317 — flagged 2 issues and suggested a fix.' },
    ],
  },
];

const CHAR_SPEED = 30;
const WORK_MS = 850; // spinner time before a tool result lands
const HOLD = 2400; // finished turn stays on screen

const ZibbyMascot = ({ size = 40 }) => (
  <svg className="lhd__mascot" width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <rect x="3" y="5" width="10" height="8" fill="#c96a3a" />
    <rect x="4" y="1" width="8" height="6" fill="#d97b4a" />
    <rect x="5" y="3" width="2" height="2" fill="#111" />
    <rect x="9" y="3" width="2" height="2" fill="#111" />
    <rect x="0" y="6" width="3" height="3" fill="#c96a3a" />
    <rect x="13" y="6" width="3" height="3" fill="#c96a3a" />
    <rect x="4" y="13" width="3" height="3" fill="#b85e30" />
    <rect x="9" y="13" width="3" height="3" fill="#b85e30" />
    <rect x="5" y="1" width="2" height="1" fill="#e89060" />
  </svg>
);

const Chevron = ({ dir }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    {dir === 'left' ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
  </svg>
);

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const BRAILLE = ['⣾', '⣽', '⣻', '⢿', '⡿', '⣟', '⣯', '⣷'];
const Spinner = () => {
  const [f, setF] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setF((x) => x + 1), 90);
    return () => clearInterval(id);
  }, []);
  return <span className="lhd__spin">{BRAILLE[f % BRAILLE.length]}</span>;
};

const LoginHeroDemo = () => {
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('ask'); // 'ask' (input active) | 'run' (streaming reply)
  const [revealed, setRevealed] = useState(0); // how many body lines are shown
  const [working, setWorking] = useState(false); // spinner on the latest tool line
  const [copied, setCopied] = useState(false);
  const [fadeKey, setFadeKey] = useState(0);
  const [manual, setManual] = useState(false);
  const cancelRef = useRef(false);
  const timeoutsRef = useRef([]);
  const hoverRef = useRef(false);
  const bodyRef = useRef(null);
  const copiedTimerRef = useRef(null);
  const enterModeRef = useRef('type');

  const clearAllTimeouts = useCallback(() => {
    cancelRef.current = true;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const delay = useCallback((ms) => new Promise((resolve) => {
    const t = setTimeout(() => { if (!cancelRef.current) resolve(); }, ms);
    timeoutsRef.current.push(t);
  }), []);

  // Keep the conversation pinned to the newest line.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [revealed, working]);

  useEffect(() => {
    cancelRef.current = false;
    const d = DEMOS[idx];
    const isManual = enterModeRef.current === 'fade';

    const run = async () => {
      setFadeKey((k) => k + 1);
      setRevealed(0);
      setWorking(false);

      if (isManual) {
        // MANUAL → finished turn, faded in, no typing / spinner.
        setManual(true);
        setTyped(d.input);
        setPhase('run');
        setRevealed(d.lines.length);
        enterModeRef.current = 'type';
        await delay(HOLD);
      } else {
        // AUTO → type the request, then stream the reply line by line.
        setManual(false);
        setTyped('');
        setPhase('ask');
        await delay(300);
        for (let ci = 1; ci <= d.input.length; ci++) {
          if (cancelRef.current) return;
          setTyped(d.input.slice(0, ci));
          await delay(CHAR_SPEED);
        }
        if (cancelRef.current) return;
        await delay(380);
        setPhase('run');
        for (let li = 0; li < d.lines.length; li++) {
          if (cancelRef.current) return;
          setRevealed(li + 1);
          if (d.lines[li].kind === 'tool') {
            setWorking(true);
            await delay(WORK_MS);
            setWorking(false);
          } else {
            await delay(d.lines[li].kind === 'say' ? 620 : 300);
          }
        }
        if (cancelRef.current) return;
        await delay(HOLD);
      }

      while (hoverRef.current && !cancelRef.current) { await delay(200); }
      if (cancelRef.current) return;
      if (!cancelRef.current) setIdx((i) => (i + 1) % DEMOS.length);
    };

    run();
    return clearAllTimeouts;
  }, [idx, delay, clearAllTimeouts]);

  const d = DEMOS[idx];
  const isCmd = d.type === 'cmd';

  const go = useCallback((step) => {
    enterModeRef.current = 'fade';
    setIdx((i) => (i + step + DEMOS.length) % DEMOS.length);
  }, []);

  const copyCommand = useCallback(() => {
    if (!isCmd) return;
    // Include the `!` — this is meant to be pasted into Claude Code, where the
    // `!` prefix runs it as a shell command (matches what's shown).
    navigator.clipboard?.writeText(`! ${d.input}`);
    setCopied(true);
    clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 1300);
  }, [isCmd, d.input]);

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  const renderLine = (ln, i) => {
    const fade = manual ? ' lhd__line--fade' : '';
    if (ln.kind === 'say') {
      return <div key={i} className={`lhd__say${fade}`}>{ln.text}</div>;
    }
    if (ln.kind === 'tool') {
      const isWorking = working && i === revealed - 1;
      return (
        <div key={i} className={`lhd__toolblock${fade}`}>
          <div className="lhd__line">
            <span className="lhd__tooldot">⏺</span>
            <span className="lhd__toolns">Zibby</span>
            <span className="lhd__toolname">{ln.tool}</span>
            {isWorking ? <Spinner /> : <span className="lhd__ok">✓</span>}
          </div>
          {!isWorking && (
            <div className="lhd__line lhd__result">
              <span className="lhd__branch">⎿</span>
              <span className="lhd__resok">✓</span>
              <span>{ln.result}</span>
            </div>
          )}
        </div>
      );
    }
    // terminal output: out | dim | ok
    const cls = ln.kind === 'ok' ? 'lhd__outok' : ln.kind === 'dim' ? 'lhd__outdim' : 'lhd__out';
    return (
      <div key={i} className={`lhd__line ${cls}${fade}`}>
        {ln.kind === 'ok' && <span className="lhd__resok">✓</span>}
        <span>{ln.text}</span>
      </div>
    );
  };

  return (
    <div
      className="lhd"
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; }}
    >
      <div className="lhd__bar">
        <span className="lhd__dot lhd__dot--r" />
        <span className="lhd__dot lhd__dot--y" />
        <span className="lhd__dot lhd__dot--g" />
        <span className="lhd__title">claude — ~/example</span>
        <div className="lhd__nav">
          <button className="lhd__arrow" onClick={() => go(-1)} aria-label="Previous"><Chevron dir="left" /></button>
          <button className="lhd__arrow" onClick={() => go(1)} aria-label="Next"><Chevron dir="right" /></button>
        </div>
      </div>

      <div className="lhd__head">
        <ZibbyMascot size={40} />
        <div className="lhd__meta">
          <div className="lhd__name">Claude Code</div>
          <div className="lhd__model">Opus 4.8 · ~/example</div>
          <div className="lhd__hint">Zibby connected · agents, deploys &amp; MCP tools</div>
        </div>
      </div>

      {/* conversation area — output for `!` commands, LLM reply for chat */}
      <div className="lhd__body" ref={bodyRef} key={fadeKey}>
        {phase === 'run' && d.lines.slice(0, revealed).map((ln, i) => renderLine(ln, i))}
      </div>

      {/* input: `!` command (cmd) or plain-English request (chat) */}
      <div className="lhd__prompt">
        <span className="lhd__chev">❯</span>
        <span className="lhd__cmd">
          {isCmd && <span className="lhd__bang">! </span>}
          <span key={fadeKey} className={`lhd__cmdtext${manual ? ' lhd__cmdtext--fade' : ''}`}>{typed}</span>
          {phase === 'ask' && <span className="lhd__cursor" />}
        </span>
        {isCmd && (
          <button
            className={`lhd__copy ${copied ? 'is-copied' : ''}`}
            onClick={copyCommand}
            aria-label="Copy command"
            title={copied ? 'Copied!' : 'Copy'}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        )}
      </div>

      <div className="lhd__foot">
        <span className="lhd__auto">▶▶ auto mode on</span>
        <span className="lhd__autohint"> (shift+tab to cycle) · ← for agents</span>
      </div>
    </div>
  );
};

export default LoginHeroDemo;
